/**
 * GridColumnHeader — sortable + filterable column header (US4/US5, Feature 039).
 * Cycles sort direction: asc → desc → null (default) on click.
 * Optionally renders ColumnFilterDropdown when filterOptions/onFilterChange are provided.
 */

import ColumnFilterDropdown from './ColumnFilterDropdown';

interface GridColumnHeaderProps {
  label: string;
  column: string;
  activeSortCol?: string | null;
  activeSortDir?: 'asc' | 'desc' | null;
  onSortChange: (col: string, dir: 'asc' | 'desc' | null) => void;
  // Optional filter props (US5)
  filterOptions?: string[];
  isFilterLoading?: boolean;
  activeFilterValues?: string[];
  onFilterChange?: (col: string, values: string[] | null) => void;
  /** 'inverted' = white text on coloured (e.g. purple) header background */
  variant?: 'default' | 'inverted';
}

export default function GridColumnHeader({
  label,
  column,
  activeSortCol,
  activeSortDir,
  onSortChange,
  filterOptions,
  isFilterLoading,
  activeFilterValues,
  onFilterChange,
  variant = 'default',
}: GridColumnHeaderProps) {
  const isActive = activeSortCol === column;
  const hasActiveFilter = !!(activeFilterValues && activeFilterValues.length > 0);

  function handleClick() {
    if (!isActive) {
      onSortChange(column, 'asc');
    } else if (activeSortDir === 'asc') {
      onSortChange(column, 'desc');
    } else {
      onSortChange(column, null);
    }
  }

  const inv = variant === 'inverted';
  return (
    <th
      className={`px-3 py-2 text-left font-medium cursor-pointer select-none whitespace-nowrap ${inv ? 'text-white/90 hover:text-white' : 'text-brand-text-secondary hover:text-brand-text-primary'}`}
      onClick={handleClick}
      aria-sort={isActive ? (activeSortDir === 'asc' ? 'ascending' : 'descending') : 'none'}
    >
      <span className="flex items-center gap-1">
        {label}
        <span className="text-xs leading-none" aria-hidden="true">
          {!isActive && <span className={inv ? 'text-white/40' : 'text-brand-text-secondary opacity-40'}>⇅</span>}
          {isActive && activeSortDir === 'asc' && <span className={inv ? 'text-white' : 'text-brand-accent'}>▲</span>}
          {isActive && activeSortDir === 'desc' && <span className={inv ? 'text-white' : 'text-brand-accent'}>▼</span>}
        </span>
        {filterOptions !== undefined && onFilterChange && (
          // Stop click from triggering sort when interacting with filter controls
          <span onClick={e => e.stopPropagation()} className="flex items-center gap-0.5">
            {hasActiveFilter && (
              <button
                type="button"
                onClick={() => onFilterChange(column, null)}
                className={`${inv ? 'text-white/80 hover:text-white' : 'text-brand-accent hover:text-red-600'} text-xs font-bold leading-none px-0.5`}
                title={`Clear ${label} filter`}
                aria-label={`Clear ${label} filter`}
              >
                ×
              </button>
            )}
            <ColumnFilterDropdown
              options={filterOptions}
              isLoading={isFilterLoading}
              activeValues={activeFilterValues}
              onFilterChange={vals => onFilterChange(column, vals)}
            />
          </span>
        )}
      </span>
    </th>
  );
}

